import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import BrandingCard from "../components/BrandingCard";
import { getAccountDetails, updateAccountDetails } from "../api/account";
import {
  fetchBrandAssets,
  uploadBrandAsset,
  deleteBrandAsset,
  type BrandAsset,
} from "../api/brandAssets";
import { useAuth } from "../context/AuthContext";

type BrandingData = {
  logo_url: string | null;
  primary_colour: string | null;
  secondary_colour: string | null;
  booking_background_color?: string | null;
  booking_background_image_url?: string | null;
};

const BrandingPage: React.FC = () => {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();

  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setError(null);
        setLoading(true);

        // ✅ Uses axios client.ts via getAccountDetails()
        const data = await getAccountDetails();

        const initial: BrandingData = {
          logo_url: data.logo_url ?? null,
          primary_colour: data.primary_colour ?? null,
          secondary_colour: data.secondary_colour ?? null,
          booking_background_color: data.booking_background_color ?? null,
          booking_background_image_url:
            data.booking_background_image_url ?? null,
        };

        setBranding(initial);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Could not load branding");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleSave = async (updates: Partial<BrandingData>) => {
    // Branding-only partial update – cast to match AccountUpdatePayload
    await updateAccountDetails(updates as any);
    setBranding((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const { data: brandAssets = [] } = useQuery<BrandAsset[]>({
    queryKey: ["brand-assets", businessId],
    queryFn: () => fetchBrandAssets(businessId!),
    enabled: !!businessId,
  });

  const assetByType = Object.fromEntries(
    brandAssets.map((a) => [a.asset_type, a])
  ) as Partial<Record<BrandAsset["asset_type"], BrandAsset>>;

  const uploadMutation = useMutation({
    mutationFn: ({
      assetType,
      file,
    }: {
      assetType: BrandAsset["asset_type"];
      file: File;
    }) => uploadBrandAsset(businessId!, assetType, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-assets", businessId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (assetType: BrandAsset["asset_type"]) =>
      deleteBrandAsset(businessId!, assetType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-assets", businessId] });
    },
  });

  if (loading && !branding) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !branding) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!branding) return null;

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Branding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure how your booking page looks: logo, colours and background.
      </Typography>

      <BrandingCard
        initialBranding={branding}
        onSave={handleSave}
        assetByType={assetByType}
        onUpload={(assetType, file) => uploadMutation.mutate({ assetType, file })}
        onDelete={(assetType) => deleteMutation.mutate(assetType)}
        uploading={uploadMutation.isPending ? uploadMutation.variables?.assetType : undefined}
        deleting={deleteMutation.isPending ? deleteMutation.variables : undefined}
      />
    </Box>
  );
};

export default BrandingPage;
